import { useQueryClient } from "@tanstack/react-query";
import { Mail, MailPlus, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import { formatDate } from "../../lib/format";
import {
  Card,
  CardHeader,
  ConfirmDialog,
  EditorPage,
  EmptyState,
  ErrorPanel,
  Notice,
  StatusPill,
} from "../../ui/components";
import { DetailPageSkeleton } from "../../ui/skeletons";
import { contactInput, contactTypeLabels, marketingStateLabels, useContact } from "./queries";

interface Invitation {
  relationshipId: string;
  customerId: string;
  email: string;
  invitationUrl?: string;
  expiresAt: string;
  emailDeliveryQueued: boolean;
}

export function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const contactQuery = useContact(contactId);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [removing, setRemoving] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const contact = contactQuery.data;

  async function invite() {
    if (!contact) return;
    setWorking(true);
    setError(undefined);
    try {
      setInvitation(
        await api<Invitation>(`/api/v1/customers/${contact.id}/portal/invitations`, {
          method: "POST",
          body: jsonBody({}),
        }),
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function setStatus(status: string) {
    if (!contact) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/contacts/${contact.id}`, {
        method: "PUT",
        body: jsonBody({ ...contactInput(contact), status }),
      });
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function remove() {
    if (!contact) return;
    setWorking(true);
    setError(undefined);
    try {
      // The version travels in the query string so a removal still loses a race it did not
      // observe: someone else editing this contact bumps the version and this call is refused
      // rather than quietly discarding their change.
      await api(`/api/v1/contacts/${contact.id}?version=${contact.version}`, { method: "DELETE" });
      setRemoving(false);
      // Only the lists are refreshed. Invalidating this record as well would send the page that
      // is still showing it back to the server for something that was just removed, and the only
      // answer to that question is a 404 nobody asked for.
      await queryClient.invalidateQueries({ queryKey: ["contacts", "list"] });
      navigate("/contacts");
    } catch (caught) {
      // A contact with bookings, quotes or invoices cannot be removed, and the server says so
      // in words. Closing the dialog first puts that answer where it can actually be read.
      setRemoving(false);
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (contactQuery.isLoading)
    return <DetailPageSkeleton title="Contact" backTo="/contacts" backLabel="Back to contacts" />;
  if (contactQuery.error) return <ErrorPanel error={contactQuery.error} />;
  if (!contact) {
    return (
      <EditorPage
        title="Contact not found"
        backTo="/contacts"
        backLabel="Back to contacts"
        description="The contact is outside your current access scope or no longer exists."
      >
        <EmptyState title="Contact not found" description="Choose another contact from the list." />
      </EditorPage>
    );
  }

  const address = billingAddressLine(contact.billingAddress);
  const consent = contact.marketingEmail;

  return (
    <EditorPage
      title={contact.displayName}
      description={
        contact.organisation
          ? `${contact.roleTitle || "Contact"} at ${contact.organisation}`
          : "Billing details, portal access, and marketing consent."
      }
      backTo="/contacts"
      backLabel="Back to contacts"
    >
      {error ? <ErrorPanel error={error} /> : null}
      <div className="stack">
        <Card>
          <CardHeader
            title="Contact record"
            actions={
              <div className="inline">
                <StatusPill value={contact.status} />
                <Link className="button secondary small" to={`/contacts/${contact.id}/edit`}>
                  <Pencil size={15} aria-hidden="true" /> Edit
                </Link>
                {/* Writing to this person starts here so whoever is composing is looking at
                    the contact they mean, rather than finding them again in a list. */}
                <Link
                  className={`button secondary small ${contact.email ? "" : "disabled"}`}
                  aria-disabled={contact.email ? undefined : true}
                  to={
                    contact.email
                      ? `/emails/new?contactId=${contact.id}`
                      : `/contacts/${contact.id}/edit`
                  }
                >
                  <Mail size={15} aria-hidden="true" /> Email
                </Link>
                <button
                  className="button secondary small"
                  type="button"
                  disabled={working || !contact.portalEnabled || !contact.email}
                  onClick={invite}
                >
                  <MailPlus size={15} aria-hidden="true" /> Invite to portal
                </button>
                <button
                  className="button secondary small"
                  type="button"
                  disabled={working}
                  onClick={() => setStatus(contact.status === "archived" ? "active" : "archived")}
                >
                  {contact.status === "archived" ? "Restore contact" : "Archive contact"}
                </button>
                <button
                  className="button secondary small"
                  type="button"
                  disabled={working}
                  onClick={() => setRemoving(true)}
                >
                  <Trash2 size={15} aria-hidden="true" /> Remove
                </button>
              </div>
            }
          />
          <dl className="definition-list">
            <dt>Contact type</dt>
            <dd>{contactTypeLabels[contact.type] ?? contact.type}</dd>
            <dt>Role or title</dt>
            <dd>{contact.roleTitle || "Not recorded"}</dd>
            <dt>Acting for</dt>
            <dd>
              {contact.organisation ||
                (contact.type === "individual" ? "Themselves" : "Not recorded")}
            </dd>
            <dt>Legal name</dt>
            <dd>{contact.legalName || "Not available"}</dd>
            <dt>Email</dt>
            <dd>{contact.email || "Not available"}</dd>
            <dt>Phone</dt>
            <dd>{contact.phone || "Not available"}</dd>
            <dt>Billing address</dt>
            <dd>{address || "Not available"}</dd>
            <dt>Tax reference</dt>
            <dd>{contact.taxReference || "Not available"}</dd>
            <dt>Tags</dt>
            <dd>{contact.tags.join(", ") || "None"}</dd>
            <dt>Portal access</dt>
            <dd>{contact.portalEnabled ? "Enabled" : "Disabled"}</dd>
            <dt>Notes</dt>
            <dd>{contact.notes || "None"}</dd>
            <dt>Added</dt>
            <dd>{formatDate(contact.createdAt)}</dd>
          </dl>
        </Card>
        <Card>
          <CardHeader
            title="Marketing consent"
            description="Held in one place with what the customer portal writes, so this screen and the person it is about cannot end up disagreeing."
          />
          {consent.state ? (
            <dl className="definition-list">
              <dt>State</dt>
              <dd>{marketingStateLabels[consent.state] ?? consent.state}</dd>
              <dt>Lawful basis</dt>
              <dd>{consent.lawfulBasis || "Not recorded"}</dd>
              <dt>How it was given</dt>
              <dd>{consent.source || "Source not recorded"}</dd>
              <dt>Recorded</dt>
              <dd>{consent.recordedAt ? formatDate(consent.recordedAt) : "Not recorded"}</dd>
            </dl>
          ) : (
            <EmptyState
              title="No consent recorded"
              description="Nobody has asked this person about marketing email. That is not the same as a refusal, and they cannot be sent marketing until they agree."
            />
          )}
        </Card>
        {invitation ? (
          invitation.emailDeliveryQueued ? (
            <Notice tone="success" title="Portal invitation email queued">
              The invitation is ready for delivery to {invitation.email}. It can only be used once
              and expires {new Date(invitation.expiresAt).toLocaleString()}.
            </Notice>
          ) : (
            <Notice tone="warning" title="Invitation created, but email was not queued">
              <span>
                Send this private fallback link to {invitation.email} securely:{" "}
                <a href={invitation.invitationUrl}>{invitation.invitationUrl}</a>. It can only be
                used once and expires {new Date(invitation.expiresAt).toLocaleString()}.
              </span>
            </Notice>
          )
        ) : null}
      </div>
      <ConfirmDialog
        open={removing}
        title="Remove this contact?"
        description={`${contact.displayName} stops appearing in the contact list. This is only possible while they have no bookings, quotes or invoices: once they have any, archive them instead so the history stays readable.`}
        confirmLabel="Remove contact"
        submitting={working}
        onClose={() => setRemoving(false)}
        onConfirm={remove}
      />
    </EditorPage>
  );
}

// The address is stored as free-form JSON because billing addresses differ by country, so it is
// assembled for display rather than assumed to have a fixed shape.
function billingAddressLine(address: Record<string, unknown>): string {
  return ["line1", "city", "region", "postalCode", "country"]
    .map((key) => String(address?.[key] ?? "").trim())
    .filter(Boolean)
    .join(", ");
}
